<template>
  <div class="property-mapper">
    <!-- Loading overlay -->
    <div v-if="loading" class="loading-overlay">
      <div class="loading-spinner">
        <div class="spinner"></div>
        <p>Loading Google Maps...</p>
      </div>
    </div>

    <!-- Error message -->
    <div v-if="error" class="error-message">
      <div class="error-content">
        <h3>Error</h3>
        <p>{{ error }}</p>
        <button @click="initializeMap" class="retry-button">Retry</button>
      </div>
    </div>

    <!-- Map container -->
    <div class="map-container" v-show="!loading && !error">
      <!-- Search panel -->
      <div class="search-panel">
        <h3>Search Properties</h3>
        <div class="search-input-container">
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Search by name or addr."
            class="search-input"
            @input="filterProperties"
          />
          <div
            v-if="searchQuery && filteredProperties.length > 0"
            class="search-results"
          >
            <div
              v-for="property in filteredProperties"
              :key="property.id"
              class="search-result-item"
              @click="zoomToProperty(property)"
            >
              <div class="search-result-content">
                <strong>{{ property.label }}</strong>
                <br />
                <small>{{ property.address }}</small>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Controls panel -->
      <div class="controls-panel">
        <h3>Property Controls</h3>
        <div v-if="totalBatches > 1" class="route-batch-info">
          <p>Batch {{ currentRouteBatch + 1 }} of {{ totalBatches }}</p>
        </div>
        <button
          @click="createMultiPropertyRoute"
          :disabled="!mapInitialized || getSelectedPropertiesCount() === 0"
          class="control-button"
        >
          Visit Selected Properties ({{ getSelectedPropertiesCount() }}/25)
        </button>
        <button
          v-if="hasMoreProperties"
          @click="createNextBatch"
          :disabled="!mapInitialized"
          class="control-button route-next-button"
        >
          Route Next ({{ getRemainingPropertiesCount() }} left)
        </button>
        <button
          @click="clearRoute"
          :disabled="!mapInitialized"
          class="control-button"
        >
          Clear Route
        </button>
        <button
          @click="shareCurrentRoute"
          :disabled="!mapInitialized || !routeInfo"
          class="control-button share-route-button"
        >
          📱 Share Route
        </button>
        <button
          @click="toggleAddPropertyForm"
          :disabled="!mapInitialized"
          class="control-button"
        >
          Add New Property
        </button>
        <button
          @click="togglePropertyList"
          :disabled="!mapInitialized"
          class="control-button"
        >
          Show as a List
        </button>
        <div class="named-routes-section">
          <select
            v-model="selectedNamedRoute"
            @change="loadNamedRoute"
            class="named-route-select"
          >
            <option value="">Select Named Route</option>
            <option
              v-for="routeName in Object.keys(namedRoutes)"
              :key="routeName"
              :value="routeName"
            >
              {{ routeName }} ({{ namedRoutes[routeName].length }} properties)
            </option>
          </select>
          <button
            v-if="selectedNamedRoute"
            @click="clearSelectedRoute"
            class="control-button clear-route-button"
          >
            Clear This Route
          </button>
          <button
            @click="toggleSaveRouteForm"
            :disabled="!mapInitialized || getSelectedPropertiesCount() === 0"
            class="control-button save-route-button"
          >
            Save Current Selection
          </button>
        </div>
      </div>

      <!-- Save Route Form -->
      <div class="save-route-form" v-show="showSaveRouteForm">
        <h3>Save Route</h3>
        <div class="form-group">
          <input
            v-model="newRouteName"
            type="text"
            placeholder="Route Name"
            class="form-input"
            @keyup.enter="saveNamedRoute"
          />
        </div>
        <div class="selected-properties-preview">
          <p>
            <strong
              >Selected Properties ({{ getSelectedPropertiesCount() }}):</strong
            >
          </p>
          <div class="selected-properties-list">
            <div
              v-for="property in getSelectedProperties()"
              :key="property.id"
              class="selected-property-item"
            >
              {{ property.label }}
            </div>
          </div>
        </div>
        <div class="form-buttons">
          <button
            @click="saveNamedRoute"
            :disabled="!newRouteName.trim()"
            class="add-button"
          >
            Save Route
          </button>
          <button @click="toggleSaveRouteForm" class="cancel-button">
            Cancel
          </button>
        </div>
      </div>

      <!-- Property List Panel -->
      <div class="property-list-panel" v-show="showPropertyList">
        <h3>Property List</h3>
        <div class="property-list-actions">
          <button @click="selectAllProperties" class="list-action-button">
            Select All
          </button>
          <button @click="deselectAllProperties" class="list-action-button">
            Deselect All
          </button>
          <button @click="togglePropertyList" class="list-action-button cancel">
            Close
          </button>
        </div>
        <div class="property-list-container">
          <div
            v-for="property in properties"
            :key="property.id"
            class="property-list-item"
            :class="{ 'office-item': property.type === 'office' }"
          >
            <div class="property-checkbox-container">
              <input
                type="checkbox"
                :id="'property-' + property.id"
                v-model="property.includeInRoute"
                :disabled="property.type === 'office'"
                class="property-checkbox"
                @change="saveSelectionsToLocalStorage"
              />
              <label
                :for="'property-' + property.id"
                class="property-label"
                :class="{ disabled: property.type === 'office' }"
              >
                <span class="property-name">{{ property.label }}</span>
                <span class="property-address">{{ property.address }}</span>
                <span class="property-type" v-if="property.type === 'office'">
                  (Office - Always Included)
                </span>
                <span class="property-type" v-else-if="property.type === ''">
                  (Excluded Type)
                </span>
              </label>
            </div>
          </div>
        </div>
      </div>

      <!-- Route info panel -->
      <div class="route-info-panel" v-show="routeInfo">
        <h3>Route Information</h3>
        <div v-if="routeInfo">
          <p><strong>Distance:</strong> {{ routeInfo.distance }}</p>
          <p><strong>Duration:</strong> {{ routeInfo.duration }}</p>
          <div
            v-if="routeInfo.instructions && routeInfo.instructions.length > 0"
          >
            <p><strong>Instructions:</strong></p>
            <ol class="instructions-list">
              <li
                v-for="(instruction, index) in routeInfo.instructions"
                :key="index"
                v-html="instruction"
              ></li>
            </ol>
          </div>
        </div>
      </div>

      <!-- Add property form -->
      <div class="add-property-form" v-show="showAddPropertyForm">
        <h3>Add New Property</h3>
        <div class="form-group">
          <input
            v-model="newProperty.label"
            type="text"
            placeholder="Property Label"
            class="form-input"
          />
        </div>
        <div class="form-group">
          <input
            v-model="newProperty.address"
            type="text"
            placeholder="Full Address"
            class="form-input"
          />
        </div>
        <div class="form-group">
          <select v-model="newProperty.type" class="form-select">
            <option value="property">Property</option>
            <option value="office">Office</option>
          </select>
        </div>
        <div class="form-buttons">
          <button
            @click="addNewProperty"
            :disabled="!newProperty.label || !newProperty.address"
            class="add-button"
          >
            Add Property
          </button>
          <button @click="toggleAddPropertyForm" class="cancel-button">
            Cancel
          </button>
        </div>
      </div>

      <!-- Google Maps container -->
      <div id="google-map" class="google-map"></div>
    </div>

    <!-- Share URL Dialog -->
    <div
      v-if="showShareUrlDialog"
      class="share-url-overlay"
      @click="closeShareUrlDialog"
    >
      <div class="share-url-dialog" @click.stop>
        <div class="share-url-header">
          <h3>Share Route URL</h3>
          <button class="close-btn" @click="closeShareUrlDialog">×</button>
        </div>
        <div class="share-url-content">
          <div class="route-details">
            <p><strong>Route:</strong> {{ shareUrlDialogData.routeName }}</p>
            <p>
              <strong>Properties:</strong>
              {{ shareUrlDialogData.selectedCount }}
            </p>
            <p><strong>Distance:</strong> {{ shareUrlDialogData.distance }}</p>
            <p><strong>Duration:</strong> {{ shareUrlDialogData.duration }}</p>
          </div>
          <div class="url-section">
            <label for="shareUrl">Click to select URL for copying:</label>
            <input
              id="shareUrl"
              ref="shareUrlInput"
              :value="shareUrlDialogData.url"
              readonly
              class="share-url-input"
              @click="selectAllUrl"
            />
          </div>
          <div
            class="share-url-status"
            v-if="shareUrlDialogData.clipboardSuccess !== undefined"
          >
            <p
              v-if="shareUrlDialogData.clipboardSuccess"
              class="success-message"
            >
              ✓ URL also copied to clipboard automatically
            </p>
            <p v-else class="info-message">
              Select the URL above and copy manually (Ctrl+C)
            </p>
          </div>
        </div>
        <div class="share-url-footer">
          <button @click="copyUrlToClipboard" class="copy-btn">Copy URL</button>
          <button @click="closeShareUrlDialog" class="close-btn-secondary">
            Close
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted, nextTick } from 'vue';

export default {
  name: 'PropertyMapper',
  setup() {
    const loading = ref(true);
    const error = ref(null);
    const mapInitialized = ref(false);
    const showAddPropertyForm = ref(false);
    const routeInfo = ref(null);
    const searchQuery = ref('');
    const filteredProperties = ref([]);
    const currentRouteBatch = ref(0);
    const totalBatches = ref(0);
    const hasMoreProperties = ref(false);
    const showPropertyList = ref(false);
    const showSaveRouteForm = ref(false);
    const newRouteName = ref('');
    const selectedNamedRoute = ref('');
    const namedRoutes = ref({});
    const highlightedRoute = ref('');
    const showShareUrlDialog = ref(false);
    const shareUrlDialogData = ref({});
    const shareUrlInput = ref(null);

    // Google Maps related
    let map = null;
    let markers = [];
    let directionsService = null;
    let directionsRenderer = null;
    let geocoder = null;

    // Property data
    const properties = ref([
      {
        id: 0,
        label: 'Carlton Office',
        address: '242 Carlton Ave SE Office Grand Rapids, MI 49506',
        type: 'office',
        lat: null,
        lng: null,
        includeInRoute: true,
      },
      {
        id: 1,
        label: 'Fulton',
        address: '1130 Fulton St E Grand Rapids, MI 49503',
        type: 'property',
        lat: null,
        lng: null,
        includeInRoute: true,
      },
      {
        id: 2,
        label: 'Fuller (114)',
        address: '114 Fuller Ave SE Grand Rapids, MI 49506',
        type: 'property',
        lat: null,
        lng: null,
        includeInRoute: true,
      },
      {
        id: 3,
        label: 'Eastern',
        address: '120 Eastern Ave SE Grand Rapids, MI 49503',
        type: 'property',
        lat: null,
        lng: null,
        includeInRoute: true,
      },
      {
        id: 4,
        label: 'Hope (1302)',
        address: '1302 Hope Street SE Grand Rapids, MI 49506',
        type: 'property',
        lat: null,
        lng: null,
        includeInRoute: true,
      },
      {
        id: 5,
        label: 'Madison (121)',
        address: '121 Madison Ave SE Grand Rapids, MI 49503',
        type: 'property',
        lat: null,
        lng: null,
        includeInRoute: true,
      },
      {
        id: 6,
        label: 'Prospect (121)',
        address: '121 Prospect Ave SE Grand Rapids, MI 49503',
        type: 'property',
        lat: null,
        lng: null,
        includeInRoute: true,
      },
      {
        id: 7,
        label: 'Prospect (124)',
        address: '124 Prospect Ave SE Grand Rapids, MI 49503',
        type: 'property',
        lat: null,
        lng: null,
        includeInRoute: true,
      },
      {
        id: 8,
        label: 'Sigsbee (1241)',
        address: '1241 Sigsbee Street SE Grand Rapids, MI 49506',
        type: 'property',
        lat: null,
        lng: null,
        includeInRoute: true,
      },
      {
        id: 9,
        label: 'Sigsbee (1245)',
        address: '1245 Sigsbee Street SE Grand Rapids, MI 49506',
        type: 'property',
        lat: null,
        lng: null,
        includeInRoute: true,
      },
      {
        id: 10,
        label: 'Sigsbee (1251)',
        address: '1251 Sigsbee Street SE Grand Rapids, MI 49506',
        type: 'property',
        lat: null,
        lng: null,
        includeInRoute: true,
      },
      {
        id: 11,
        label: 'Lake (1301)',
        address: '1301 Lake Drive SE Grand Rapids, MI 49506',
        type: 'property',
        lat: null,
        lng: null,
        includeInRoute: true,
      },
      {
        id: 12,
        label: 'Hope (1203)',
        address: '1203 Hope Street SE Grand Rapids, MI 49506',
        type: 'property',
        lat: null,
        lng: null,
        includeInRoute: true,
      },
      {
        id: 13,
        label: 'Lake (1307)',
        address: '1307 Lake Drive SE Grand Rapids, MI 49506',
        type: 'property',
        lat: null,
        lng: null,
        includeInRoute: true,
      },
      {
        id: 14,
        label: 'Sigsbee (1315)',
        address: '1315 Sigsbee Street SE Grand Rapids, MI 49506',
        type: 'property',
        lat: null,
        lng: null,
        includeInRoute: true,
      },
      {
        id: 15,
        label: 'Sigsbee (1410)',
        address: '1410 Sigsbee Street SE Grand Rapids, MI 49506',
        type: 'property',
        lat: null,
        lng: null,
        includeInRoute: true,
      },
      {
        id: 16,
        label: 'Union (142)',
        address: '142 Union Ave NE Grand Rapids, MI 49503',
        type: 'property',
        lat: null,
        lng: null,
        includeInRoute: true,
      },
      {
        id: 17,
        label: 'Lake (1572)',
        address: '1572 Lake Drive SE Grand Rapids, MI 49506',
        type: 'property',
        lat: null,
        lng: null,
        includeInRoute: true,
      },
      {
        id: 18,
        label: 'Prospect (161)',
        address: '161 Prospect Ave NE Grand Rapids, MI 49503',
        type: 'property',
        lat: null,
        lng: null,
        includeInRoute: true,
      },
      {
        id: 19,
        label: 'Prospect (163)',
        address: '163 Prospect Ave NE Grand Rapids, MI 49503',
        type: 'property',
        lat: null,
        lng: null,
        includeInRoute: true,
      },
      {
        id: 20,
        label: 'Fuller (206)',
        address: '206 Fuller Ave SE Grand Rapids, MI 49506',
        type: 'property',
        lat: null,
        lng: null,
        includeInRoute: true,
      },
      {
        id: 21,
        label: 'John (216)',
        address: '216 John Street NE Grand Rapids, MI 49503',
        type: 'property',
        lat: null,
        lng: null,
        includeInRoute: true,
      },
      {
        id: 22,
        label: 'John (218)',
        address: '218 John Street NE Grand Rapids, MI 49503',
        type: 'property',
        lat: null,
        lng: null,
        includeInRoute: true,
      },
      {
        id: 23,
        label: 'Washington',
        address: '254 Washington Street SE Grand Rapids, MI 49503',
        type: 'property',
        lat: null,
        lng: null,
        includeInRoute: true,
      },
      {
        id: 24,
        label: 'Rockhill',
        address: '2639 Rockhill Drive NE Grand Rapids, MI 49525',
        type: '',
        lat: null,
        lng: null,
        includeInRoute: false,
      },
      {
        id: 25,
        label: 'Logan',
        address: '348 Logan Street SE Grand Rapids, MI 49503',
        type: 'property',
        lat: null,
        lng: null,
        includeInRoute: true,
      },
      {
        id: 26,
        label: 'College (401)',
        address: '401 College Ave SE Grand Rapids, MI 49503',
        type: 'property',
        lat: null,
        lng: null,
        includeInRoute: true,
      },
      {
        id: 27,
        label: 'College (403)',
        address: '403 College Ave SE Grand Rapids, MI 49503',
        type: 'property',
        lat: null,
        lng: null,
        includeInRoute: true,
      },
      {
        id: 28,
        label: 'Benjamin',
        address: '417 Benjamin Ave SE Grand Rapids, MI 49506',
        type: 'property',
        lat: null,
        lng: null,
        includeInRoute: true,
      },
      {
        id: 29,
        label: 'Fountain',
        address: '442 Fountain Street NE Grand Rapids, MI 49503',
        type: 'property',
        lat: null,
        lng: null,
        includeInRoute: true,
      },
      {
        id: 30,
        label: 'Wealthy',
        address: '456 Wealthy Street SE Grand Rapids, MI 49503',
        type: 'property',
        lat: null,
        lng: null,
        includeInRoute: true,
      },
      {
        id: 31,
        label: 'Diamond',
        address: '479 Diamond Avenue NE Grand Rapids, MI 49503',
        type: 'property',
        lat: null,
        lng: null,
        includeInRoute: true,
      },
      {
        id: 32,
        label: 'Byron Shores',
        address: '6842 Byron Shores CT SW Byron Center, MI 49315',
        type: '',
        lat: null,
        lng: null,
        includeInRoute: false,
      },
      {
        id: 33,
        label: 'Lyon (833)',
        address: '833 Lyon Street NE Grand Rapids, MI 49503',
        type: 'property',
        lat: null,
        lng: null,
        includeInRoute: true,
      },
      {
        id: 34,
        label: 'Calvin',
        address: '913 Calvin Ave SE Grand Rapids, MI 49506',
        type: 'property',
        lat: null,
        lng: null,
        includeInRoute: true,
      },
      {
        id: 35,
        label: '64th Barn',
        address: '6898 64th Street SW Barn Byron Center, MI 49315',
        type: '',
        lat: null,
        lng: null,
        includeInRoute: false,
      },
      {
        id: 36,
        label: '64th (2930)',
        address: '2930 64th Street SW Byron Center, MI 49315',
        type: '',
        lat: null,
        lng: null,
        includeInRoute: false,
      },
      {
        id: 37,
        label: 'Richard',
        address: '315 Richard Ter SE Grand Rapids, MI 49506',
        type: 'office',
        lat: null,
        lng: null,
        includeInRoute: true,
      },
      {
        id: 38,
        label: 'Union (332)',
        address: '332 Union Ave NE Grand Rapids, MI 49503',
        type: 'property',
        lat: null,
        lng: null,
        includeInRoute: true,
      },
      {
        id: 39,
        label: 'Michigan',
        address: '600 Michigan Street NE Grand Rapids, MI 49503',
        type: 'property',
        lat: null,
        lng: null,
        includeInRoute: true,
      },
      {
        id: 40,
        label: 'Lyon (411)',
        address: '411 Lyon Street NE Grand Rapids, MI 49503',
        type: 'property',
        lat: null,
        lng: null,
        includeInRoute: true,
      },
      {
        id: 41,
        label: 'Sigsbee (1237)',
        address: '1237 Sigsbee Street SE Grand Rapids, MI 49506',
        type: 'property',
        lat: null,
        lng: null,
        includeInRoute: true,
      },
      {
        id: 42,
        label: 'Lyon (405)',
        address: '405 Lyon Street NE Grand Rapids, MI 49503',
        type: 'property',
        lat: null,
        lng: null,
        includeInRoute: true,
      },
    ]);

    const newProperty = ref({
      label: '',
      address: '',
      type: 'property',
    });

    // Check if showAll URL parameter is set
    const shouldShowAllProperties = () => {
      const urlParams = new URLSearchParams(window.location.search);
      return urlParams.get('showAll') === '1';
    };

    // Load Google Maps API
    const loadGoogleMapsAPI = () => {
      return new Promise((resolve, reject) => {
        if (window.google && window.google.maps) {
          resolve();
          return;
        }

        const apiKey = process.env.VUE_APP_GOOGLE_MAPS_API_KEY;
        if (!apiKey) {
          reject(
            new Error(
              'Google Maps API key not found. Please set VUE_APP_GOOGLE_MAPS_API_KEY in environment variables.',
            ),
          );
          return;
        }

        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=geometry`;
        script.onload = resolve;
        script.onerror = () =>
          reject(new Error('Failed to load Google Maps API'));
        document.head.appendChild(script);
      });
    };

    // Geocode address
    const geocodeAddress = (address) => {
      return new Promise((resolve, reject) => {
        geocoder.geocode({ address: address }, (results, status) => {
          if (status === 'OK') {
            const location = results[0].geometry.location;
            resolve({
              lat: location.lat(),
              lng: location.lng(),
            });
          } else {
            reject(`Geocoding failed: ${status}`);
          }
        });
      });
    };

    // Get marker icon based on property type and highlight state
    const getMarkerIcon = (type, isHighlighted = false) => {
      const iconBase = 'https://maps.google.com/mapfiles/';

      // If highlighted, use different icons with border effect
      if (isHighlighted) {
        switch (type) {
          case 'office':
            return {
              url: iconBase + 'kml/paddle/red-stars.png',
              scaledSize: new window.google.maps.Size(50, 50), // Larger for highlight
            };
          case 'property':
            return {
              url: iconBase + 'kml/paddle/ylw-blank.png', // Yellow for highlight
              scaledSize: new window.google.maps.Size(45, 45), // Larger for highlight
            };
          case '':
            return {
              url: iconBase + 'kml/paddle/grn-blank.png',
              scaledSize: new window.google.maps.Size(45, 45), // Larger for highlight
            };
          default:
            return {
              url: iconBase + 'kml/paddle/grn-blank.png',
              scaledSize: new window.google.maps.Size(45, 45), // Larger for highlight
            };
        }
      }

      // Normal state icons
      switch (type) {
        case 'office':
          return {
            url: iconBase + 'kml/paddle/red-stars.png',
            scaledSize: new window.google.maps.Size(40, 40),
          };
        case 'property':
          return {
            url: iconBase + 'kml/paddle/blu-blank.png',
            scaledSize: new window.google.maps.Size(35, 35),
          };
        case '':
          return {
            url: iconBase + 'kml/paddle/grn-blank.png',
            scaledSize: new window.google.maps.Size(35, 35),
          };
        default:
          return {
            url: iconBase + 'kml/paddle/grn-blank.png',
            scaledSize: new window.google.maps.Size(35, 35),
          };
      }
    };

    // Get all properties in a specific named route
    const getPropertiesInRoute = (routeName) => {
      if (!namedRoutes.value[routeName]) return [];
      return namedRoutes.value[routeName];
    };

    // Sync includeInRoute properties with the highlighted route
    const syncIncludeInRouteWithHighlightedRoute = (routeName) => {
      if (!routeName || !namedRoutes.value[routeName]) {
        return;
      }

      const routePropertyIds = namedRoutes.value[routeName];

      // First deselect all properties
      properties.value.forEach((property) => {
        if (property.type === 'property') {
          property.includeInRoute = false;
        }
      });

      // Then select properties in the highlighted route
      routePropertyIds.forEach((propertyId) => {
        const property = properties.value.find((p) => p.id === propertyId);
        if (property && property.type === 'property') {
          property.includeInRoute = true;
        }
      });

      saveSelectionsToLocalStorage();
    };

    // Highlight all markers in a specific route
    const highlightRoute = (routeName) => {
      if (highlightedRoute.value === routeName) {
        // If clicking the same route, unhighlight and clear selection
        highlightedRoute.value = '';
        selectedNamedRoute.value = '';
        saveLastSelectedRouteToLocalStorage();
        updateAllMarkerIcons();
        // Clear all includeInRoute when unhighlighting
        deselectAllProperties();
        return;
      }

      highlightedRoute.value = routeName;
      updateAllMarkerIcons();

      // Synchronize selectedNamedRoute when highlighting changes
      if (routeName && namedRoutes.value[routeName]) {
        selectedNamedRoute.value = routeName;
        saveLastSelectedRouteToLocalStorage();

        // Sync includeInRoute properties with the highlighted route
        syncIncludeInRouteWithHighlightedRoute(routeName);
      }
    };

    // Update all marker icons based on current highlight state
    const updateAllMarkerIcons = () => {
      const highlightedPropertyIds = highlightedRoute.value
        ? getPropertiesInRoute(highlightedRoute.value)
        : [];

      markers.forEach((marker) => {
        const property = properties.value.find(
          (p) =>
            p.lat === marker.getPosition().lat() &&
            p.lng === marker.getPosition().lng(),
        );

        if (property) {
          const isHighlighted = highlightedPropertyIds.includes(property.id);
          marker.setIcon(getMarkerIcon(property.type, isHighlighted));
        }
      });
    };

    // Create markers for all properties
    const createMarkers = () => {
      const showAll = shouldShowAllProperties();

      properties.value.forEach((property) => {
        // Skip properties with empty type unless showAll=1 is set
        if (property.type === '' && !showAll) {
          return;
        }

        if (property.lat && property.lng) {
          const marker = new window.google.maps.Marker({
            position: { lat: property.lat, lng: property.lng },
            map: map,
            title: property.label,
            icon: getMarkerIcon(property.type),
          });

          const infoWindow = new window.google.maps.InfoWindow();

          marker.addListener('click', () => {
            markers.forEach((m) => m.infoWindow && m.infoWindow.close());

            // Check if this property is part of any named routes
            const containingRoutes = getRoutesContainingProperty(property.id);

            // If property is in named routes, highlight the first one and sync the selected route
            if (containingRoutes.length > 0) {
              const routeToHighlight = containingRoutes[0];
              highlightRoute(routeToHighlight);

              // Synchronize selectedNamedRoute with the highlighted route
              selectedNamedRoute.value = routeToHighlight;
              saveLastSelectedRouteToLocalStorage();

              // Update includeInRoute properties to match the highlighted route
              syncIncludeInRouteWithHighlightedRoute(routeToHighlight);
            } else {
              // Clear any existing highlights if property is not in a route
              if (highlightedRoute.value) {
                highlightedRoute.value = '';
                updateAllMarkerIcons();
              }

              // Clear selected route when clicking a property not in any route
              if (selectedNamedRoute.value) {
                selectedNamedRoute.value = '';
                saveLastSelectedRouteToLocalStorage();
              }

              // Clear all includeInRoute when no route is selected
              deselectAllProperties();
            }

            // Generate content dynamically to reflect current state
            const routeOptions = Object.keys(namedRoutes.value)
              .map(
                (routeName) =>
                  `<option value="${routeName}">${routeName}</option>`,
              )
              .join('');

            const routesList =
              containingRoutes.length > 0
                ? `<p><small><strong>In routes:</strong> ${containingRoutes.map((routeName) => `${routeName} (${namedRoutes.value[routeName] ? namedRoutes.value[routeName].length : 0})`).join(', ')}</small></p>`
                : '';

            const content = `
              <div style="max-width: 250px; padding: 15px; border-radius: 8px; box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15); background: rgba(255, 255, 255, 0.85);">
                <h3 style="margin: 0 0 8px 0; font-size: 16px; color: #333;">${property.label}</h3>
                <p style="margin: 0 0 8px 0; font-size: 12px; color: #000;">${property.address}</p>
                ${routesList}
                ${
                  containingRoutes.length > 0
                    ? `<p style="font-size: 11px; color: #0066cc; font-style: italic; margin: 4px 0;">
                         <strong>Route highlighted:</strong> ${containingRoutes[0]}
                       </p>`
                    : ''
                }
                ${
                  property.type !== 'office' && property.type !== ''
                    ? `
                    <div style="margin: 8px 0; border-top: 1px solid #eee; padding-top: 8px;">
                      <label style="font-size: 12px; font-weight: bold; margin-bottom: 4px; display: block;">Add to Named Route:</label>
                      <div style="display: flex; gap: 5px; align-items: center;">
                        <select id="routeSelect_${property.id}" style="flex: 1; padding: 4px; font-size: 11px; border: 1px solid #ddd; border-radius: 3px; background: rgba(255, 255, 255, 0.9);">
                          <option value="">Select Route</option>
                          <option value="__NEW_ROUTE__">New route...</option>
                          ${routeOptions}
                        </select>
                        <button onclick="window.addToSelectedRoute('${property.id}')"
                                style="padding: 4px 8px; background: #34a853; color: white; border: none; border-radius: 3px; cursor: pointer; font-size: 11px; white-space: nowrap;">
                          Add
                        </button>
                      </div>
                    </div>
                    ${
                      containingRoutes.length > 0
                        ? `
                    <div style="margin: 8px 0;">
                      <label style="font-size: 12px; font-weight: bold; margin-bottom: 4px; display: block;">Remove from Route:</label>
                      <div style="display: flex; gap: 5px; align-items: center;">
                        <select id="removeRouteSelect_${property.id}" style="flex: 1; padding: 4px; font-size: 11px; border: 1px solid #ddd; border-radius: 3px; background: rgba(255, 255, 255, 0.9);">
                          <option value="">Select Route</option>
                          ${containingRoutes.map((route) => `<option value="${route}">${route}</option>`).join('')}
                        </select>
                        <button onclick="window.removeFromSelectedRoute('${property.id}')"
                                style="padding: 4px 8px; background: #ea4335; color: white; border: none; border-radius: 3px; cursor: pointer; font-size: 11px; white-space: nowrap;">
                          Remove
                        </button>
                      </div>
                    </div>`
                        : ''
                    }
                    `
                    : property.type === 'office'
                      ? '<p style="font-size: 12px; color: #000; margin: 8px 0 0 0;"><em>Office location</em></p>'
                      : ''
                }
              </div>
            `;

            infoWindow.setContent(content);
            infoWindow.open(map, marker);
          });

          marker.infoWindow = infoWindow;
          markers.push(marker);
        }
      });
    };

    // Fit map to show all markers
    const fitMapToMarkers = () => {
      if (markers.length === 0) return;

      const bounds = new window.google.maps.LatLngBounds();
      markers.forEach((marker) => {
        bounds.extend(marker.getPosition());
      });
      map.fitBounds(bounds);
    };

    // Geocode all addresses
    const geocodeAddresses = async () => {
      const showAll = shouldShowAllProperties();

      for (let property of properties.value) {
        // Skip properties with empty type unless showAll=1 is set
        if (property.type === '' && !showAll) {
          console.log(
            `Skipping geocoding for ${property.label} (empty type, showAll=false)`,
          );
          continue;
        }

        try {
          const result = await geocodeAddress(property.address);
          property.lat = result.lat;
          property.lng = result.lng;
          console.log(
            `Geocoded ${property.label}: ${property.lat}, ${property.lng}`,
          );
        } catch (error) {
          console.error(`Failed to geocode ${property.label}:`, error);
        }
      }
    };

    // Calculate route
    const calculateRoute = (request) => {
      return new Promise((resolve, reject) => {
        directionsService.route(request, (result, status) => {
          if (status === 'OK') {
            resolve(result);
          } else {
            reject(`Directions request failed: ${status}`);
          }
        });
      });
    };

    // Display route information
    const displayRouteInfo = (result) => {
      const route = result.routes[0];
      const leg = route.legs[0];

      routeInfo.value = {
        distance: leg.distance.text,
        duration: leg.duration.text,
        instructions: leg.steps.map((step) => step.instructions),
      };
    };

    // Create route from office to specific property
    const createRoute = async (propertyId) => {
      const office = properties.value.find((p) => p.type === 'office');
      const destination = properties.value.find((p) => p.id === propertyId);

      if (!office || !destination) {
        console.error('Office or destination property not found');
        return;
      }

      const request = {
        origin: { lat: office.lat, lng: office.lng },
        destination: { lat: destination.lat, lng: destination.lng },
        travelMode: window.google.maps.TravelMode.DRIVING,
        optimizeWaypoints: true,
      };

      try {
        const result = await calculateRoute(request);
        directionsRenderer.setDirections(result);
        displayRouteInfo(result);
      } catch (error) {
        console.error('Route calculation failed:', error);
        error.value = 'Failed to calculate route: ' + error;
      }
    };

    // Create multi-property route
    const createMultiPropertyRoute = async () => {
      const office = properties.value.find((p) => p.type === 'office');
      const propertyList = properties.value.filter(
        (p) =>
          p.type === 'property' &&
          p.type !== '' &&
          p.lat &&
          p.lng &&
          p.includeInRoute,
      );

      if (!office || propertyList.length === 0) {
        error.value = 'Office or included properties not found';
        return;
      }

      // Close all open info windows when starting route creation
      closeAllInfoWindows();

      // Reset batch tracking
      currentRouteBatch.value = 0;

      // Google Maps API limit is 25 waypoints (plus origin and destination)
      const MAX_WAYPOINTS = 25;

      // Calculate total batches
      totalBatches.value = Math.ceil(propertyList.length / MAX_WAYPOINTS);
      hasMoreProperties.value = propertyList.length > MAX_WAYPOINTS;

      if (propertyList.length <= MAX_WAYPOINTS) {
        // If we have 25 or fewer properties, create a single route
        const waypoints = propertyList.map((property) => ({
          location: { lat: property.lat, lng: property.lng },
          stopover: true,
        }));

        const request = {
          origin: { lat: office.lat, lng: office.lng },
          destination: { lat: office.lat, lng: office.lng },
          waypoints: waypoints,
          travelMode: window.google.maps.TravelMode.DRIVING,
          optimizeWaypoints: true,
        };

        try {
          const result = await calculateRoute(request);
          directionsRenderer.setDirections(result);
          displayRouteInfo(result);
        } catch (error) {
          console.error('Multi-property route calculation failed:', error);
          error.value = 'Failed to calculate multi-property route: ' + error;
        }
      } else {
        // If we have more than 25 properties, show the first 25 with a warning
        await createRouteBatch(0);
      }
    };

    // Create route for a specific batch
    const createRouteBatch = async (batchIndex) => {
      const office = properties.value.find((p) => p.type === 'office');
      const propertyList = properties.value.filter(
        (p) =>
          p.type === 'property' &&
          p.type !== '' &&
          p.lat &&
          p.lng &&
          p.includeInRoute,
      );

      // Close all open info windows when creating new batch
      closeAllInfoWindows();

      const MAX_WAYPOINTS = 25;
      const startIndex = batchIndex * MAX_WAYPOINTS;
      const endIndex = Math.min(
        startIndex + MAX_WAYPOINTS,
        propertyList.length,
      );
      const batchProperties = propertyList.slice(startIndex, endIndex);

      currentRouteBatch.value = batchIndex;
      hasMoreProperties.value = endIndex < propertyList.length;

      const waypoints = batchProperties.map((property) => ({
        location: { lat: property.lat, lng: property.lng },
        stopover: true,
      }));

      const request = {
        origin: { lat: office.lat, lng: office.lng },
        destination: { lat: office.lat, lng: office.lng },
        waypoints: waypoints,
        travelMode: window.google.maps.TravelMode.DRIVING,
        optimizeWaypoints: true,
      };

      try {
        const result = await calculateRoute(request);
        directionsRenderer.setDirections(result);
        displayRouteInfo(result);

        // Show info about current batch
        const currentBatchSize = batchProperties.length;
        const remainingProperties = propertyList.length - endIndex;

        console.log(
          `Showing route for batch ${batchIndex + 1} of ${totalBatches.value} (${currentBatchSize} properties). ${remainingProperties} properties remaining.`,
        );

        if (remainingProperties > 0) {
          alert(
            `Route created for batch ${batchIndex + 1} of ${totalBatches.value} (${currentBatchSize} properties). ${remainingProperties} properties remaining. Click "Route Next" to continue.`,
          );
        } else {
          alert(
            `Route created for final batch ${batchIndex + 1} of ${totalBatches.value} (${currentBatchSize} properties). All properties have been routed.`,
          );
        }
      } catch (error) {
        console.error('Multi-property route calculation failed:', error);
        error.value = 'Failed to calculate multi-property route: ' + error;
      }
    };

    // Create next batch of properties
    const createNextBatch = async () => {
      if (hasMoreProperties.value) {
        await createRouteBatch(currentRouteBatch.value + 1);
      }
    };

    // Get remaining properties count
    const getRemainingPropertiesCount = () => {
      const propertyList = properties.value.filter(
        (p) =>
          p.type === 'property' &&
          p.type !== '' &&
          p.lat &&
          p.lng &&
          p.includeInRoute,
      );
      const MAX_WAYPOINTS = 25;
      const currentEndIndex = (currentRouteBatch.value + 1) * MAX_WAYPOINTS;
      return Math.max(0, propertyList.length - currentEndIndex);
    };

    // Close all open info windows
    const closeAllInfoWindows = () => {
      try {
        if (markers && markers.length > 0) {
          markers.forEach((marker) => {
            if (marker.infoWindow) {
              marker.infoWindow.close();
            }
          });
          console.log('All info windows closed');
        }
      } catch (error) {
        console.error('Error closing info windows:', error);
      }
    };

    // Clear route
    const clearRoute = () => {
      try {
        if (directionsRenderer) {
          // Properly clear directions by removing from map and recreating
          directionsRenderer.setMap(null);
          directionsRenderer = new window.google.maps.DirectionsRenderer({
            suppressMarkers: false,
            draggable: true,
          });
          directionsRenderer.setMap(map);
        }
        routeInfo.value = null;

        // Reset batch tracking
        currentRouteBatch.value = 0;
        totalBatches.value = 0;
        hasMoreProperties.value = false;

        // Clear route highlighting and selection state
        highlightedRoute.value = '';
        selectedNamedRoute.value = '';
        saveLastSelectedRouteToLocalStorage();
        updateAllMarkerIcons();

        // Clear all includeInRoute properties
        deselectAllProperties();

        // Close all open info windows (popup dialogs)
        closeAllInfoWindows();

        console.log('Route cleared successfully');
      } catch (error) {
        console.error('Error clearing route:', error);

        // Fallback: try to recreate directions renderer completely
        try {
          if (map) {
            directionsRenderer = new window.google.maps.DirectionsRenderer({
              suppressMarkers: false,
              draggable: true,
            });
            directionsRenderer.setMap(map);
          }
        } catch (fallbackError) {
          console.error(
            'Fallback directions renderer creation failed:',
            fallbackError,
          );
        }

        // Set routeInfo to null even if directions renderer fails
        routeInfo.value = null;
        currentRouteBatch.value = 0;
        totalBatches.value = 0;
        hasMoreProperties.value = false;

        // Clear route highlighting and selection state even on error
        highlightedRoute.value = '';
        selectedNamedRoute.value = '';
        saveLastSelectedRouteToLocalStorage();
        updateAllMarkerIcons();

        // Still try to close info windows even if other operations fail
        closeAllInfoWindows();
      }
    };

    // Generate Google Maps URL for route sharing
    const generateRouteShareURL = () => {
      const office = properties.value.find((p) => p.type === 'office');
      const selectedProperties = properties.value.filter(
        (p) => p.type === 'property' && p.includeInRoute && p.lat && p.lng,
      );

      if (!office || selectedProperties.length === 0) {
        return null;
      }

      // Build the Google Maps URL
      const baseUrl = 'https://www.google.com/maps/dir/?api=1';
      const params = new URLSearchParams({
        origin: office.address,
        destination: office.address, // Return to office
        travelmode: 'driving',
      });

      // Add waypoints (limited to 25 by Google Maps URL API)
      if (selectedProperties.length > 0) {
        const waypoints = selectedProperties
          .slice(0, 25) // Limit to 25 waypoints
          .map((property) => property.address)
          .join('|');
        params.append('waypoints', waypoints);
      }

      return `${baseUrl}&${params.toString()}`;
    };

    // Share current route
    const shareCurrentRoute = async () => {
      if (!routeInfo.value) {
        alert('No route to share. Please create a route first.');
        return;
      }

      const routeUrl = generateRouteShareURL();
      if (!routeUrl) {
        alert(
          'Unable to generate route URL. Please ensure you have properties selected.',
        );
        return;
      }

      const selectedCount = getSelectedPropertiesCount();
      const routeName = selectedNamedRoute.value || 'Property Route';

      const shareData = {
        title: `${routeName} - Property Visits`,
        text: `Route visiting ${selectedCount} properties\nDistance: ${routeInfo.value.distance}\nDuration: ${routeInfo.value.duration}`,
        url: routeUrl,
      };

      try {
        // Try Web Share API first (mobile devices)
        if (
          navigator.share &&
          navigator.canShare &&
          navigator.canShare(shareData)
        ) {
          await navigator.share(shareData);
          console.log('Route shared successfully via Web Share API');
        } else {
          // Fallback to clipboard
          await navigator.clipboard.writeText(routeUrl);
          alert(
            `Route URL copied to clipboard!\n\nRoute: ${routeName}\nProperties: ${selectedCount}\nDistance: ${routeInfo.value.distance}\nDuration: ${routeInfo.value.duration}`,
          );
        }
      } catch (error) {
        console.error('Error sharing route:', error);

        // Final fallback - show the URL in a custom dialog for easy copying
        const selectedCount = getSelectedPropertiesCount();
        const routeName = selectedNamedRoute.value || 'Property Route';

        shareUrlDialogData.value = {
          url: routeUrl,
          routeName: routeName,
          selectedCount: selectedCount,
          distance: routeInfo.value.distance,
          duration: routeInfo.value.duration,
        };

        showShareUrlDialog.value = true;

        // Try copying to clipboard as backup
        try {
          await navigator.clipboard.writeText(routeUrl);
          shareUrlDialogData.value.clipboardSuccess = true;
        } catch (clipboardError) {
          shareUrlDialogData.value.clipboardSuccess = false;
        }
      }
    };

    // Close share URL dialog
    const closeShareUrlDialog = () => {
      showShareUrlDialog.value = false;
      shareUrlDialogData.value = {};
    };

    // Select all text in the URL input
    const selectAllUrl = (event) => {
      event.target.select();
      event.target.setSelectionRange(0, 99999); // For mobile devices
    };

    // Copy URL to clipboard from dialog
    const copyUrlToClipboard = async () => {
      try {
        await navigator.clipboard.writeText(shareUrlDialogData.value.url);
        shareUrlDialogData.value.clipboardSuccess = true;
        // Auto-close dialog after successful copy
        setTimeout(() => {
          closeShareUrlDialog();
        }, 1500);
      } catch (error) {
        console.error('Failed to copy URL:', error);
        shareUrlDialogData.value.clipboardSuccess = false;
        // Fall back to selecting the text for manual copy
        const input = document.getElementById('shareUrl');
        if (input) {
          input.select();
          input.setSelectionRange(0, 99999);
        }
      }
    };

    // Filter properties based on search query
    const filterProperties = () => {
      if (!searchQuery.value || searchQuery.value.length < 2) {
        filteredProperties.value = [];
        return;
      }

      const query = searchQuery.value.toLowerCase();
      const showAll = shouldShowAllProperties();

      filteredProperties.value = properties.value
        .filter((property) => {
          // Skip properties with empty type unless showAll=1 is set
          if (property.type === '' && !showAll) {
            return false;
          }

          return (
            property.label.toLowerCase().includes(query) ||
            property.address.toLowerCase().includes(query)
          );
        })
        .slice(0, 10); // Limit to 10 results
    };

    // Zoom to specific property
    const zoomToProperty = (property) => {
      if (!property.lat || !property.lng) {
        console.error('Property coordinates not available');
        return;
      }

      // Clear search
      searchQuery.value = '';
      filteredProperties.value = [];

      // Zoom to property
      map.setCenter({ lat: property.lat, lng: property.lng });
      map.setZoom(18);

      // Find and trigger the marker click
      const marker = markers.find((m) => m.getTitle() === property.label);
      if (marker && marker.infoWindow) {
        // Close all other info windows
        markers.forEach((m) => m.infoWindow && m.infoWindow.close());
        // Open this property's info window
        marker.infoWindow.open(map, marker);
      }
    };

    // Create route from search result
    const createRouteFromSearch = async (property) => {
      if (!property.lat || !property.lng) {
        console.error('Property coordinates not available');
        return;
      }

      // Clear search
      searchQuery.value = '';
      filteredProperties.value = [];

      // Create route using the property ID
      await createRoute(property.id);
    };

    // Toggle add property form
    const toggleAddPropertyForm = () => {
      showAddPropertyForm.value = !showAddPropertyForm.value;
      if (!showAddPropertyForm.value) {
        newProperty.value = {
          label: '',
          address: '',
          type: 'property',
        };
      }
    };

    // Add new property
    const addNewProperty = async () => {
      if (!newProperty.value.label || !newProperty.value.address) {
        error.value = 'Please fill in all fields';
        return;
      }

      try {
        const coords = await geocodeAddress(newProperty.value.address);
        const property = {
          id: 'property_' + Date.now(),
          label: newProperty.value.label,
          address: newProperty.value.address,
          type: newProperty.value.type,
          lat: coords.lat,
          lng: coords.lng,
          includeInRoute: true,
        };

        properties.value.push(property);

        // Create marker for new property
        const marker = new window.google.maps.Marker({
          position: { lat: property.lat, lng: property.lng },
          map: map,
          title: property.label,
          icon: getMarkerIcon(property.type),
        });

        markers.push(marker);
        fitMapToMarkers();

        toggleAddPropertyForm();
      } catch (error) {
        console.error('Failed to add property:', error);
        error.value =
          'Failed to add property. Please check the address and try again.';
      }
    };

    // Toggle property list panel
    const togglePropertyList = () => {
      showPropertyList.value = !showPropertyList.value;
    };

    // Select all properties for routing
    const selectAllProperties = () => {
      properties.value.forEach((property) => {
        if (property.type === 'property') {
          property.includeInRoute = true;
        }
      });
      saveSelectionsToLocalStorage();
    };

    // Deselect all properties from routing
    const deselectAllProperties = () => {
      properties.value.forEach((property) => {
        if (property.type === 'property') {
          property.includeInRoute = false;
        }
      });
      saveSelectionsToLocalStorage();
    };

    // Get selected properties count
    const getSelectedPropertiesCount = () => {
      return properties.value.filter(
        (p) => p.includeInRoute && p.type === 'property',
      ).length;
    };

    // Get selected properties
    const getSelectedProperties = () => {
      return properties.value.filter(
        (p) => p.includeInRoute && p.type === 'property',
      );
    };

    // Toggle save route form
    const toggleSaveRouteForm = () => {
      showSaveRouteForm.value = !showSaveRouteForm.value;
      if (!showSaveRouteForm.value) {
        newRouteName.value = '';
      }
    };

    // Save named route
    const saveNamedRoute = () => {
      if (!newRouteName.value.trim()) {
        alert('Please enter a route name');
        return;
      }

      const selectedPropertyIds = properties.value
        .filter((p) => p.includeInRoute && p.type === 'property')
        .map((p) => p.id);

      if (selectedPropertyIds.length === 0) {
        alert('Please select at least one property');
        return;
      }

      namedRoutes.value[newRouteName.value.trim()] = selectedPropertyIds;
      saveNamedRoutesToLocalStorage();

      // Set this as the selected route
      selectedNamedRoute.value = newRouteName.value.trim();
      saveLastSelectedRouteToLocalStorage();

      alert(
        `Route "${newRouteName.value.trim()}" saved with ${selectedPropertyIds.length} properties`,
      );
      toggleSaveRouteForm();
    };

    // Load named route
    const loadNamedRoute = async () => {
      if (!selectedNamedRoute.value) {
        // Clear highlighting when no route is selected
        highlightedRoute.value = '';
        updateAllMarkerIcons();
        return;
      }

      const routePropertyIds = namedRoutes.value[selectedNamedRoute.value];
      if (!routePropertyIds) {
        alert('Route not found');
        return;
      }

      // First deselect all properties
      properties.value.forEach((property) => {
        if (property.type === 'property') {
          property.includeInRoute = false;
        }
      });

      // Then select properties in the named route
      routePropertyIds.forEach((propertyId) => {
        const property = properties.value.find((p) => p.id === propertyId);
        if (property && property.type === 'property') {
          property.includeInRoute = true;
        }
      });

      // Highlight the loaded route markers
      highlightedRoute.value = selectedNamedRoute.value;
      updateAllMarkerIcons();

      saveSelectionsToLocalStorage();
      saveLastSelectedRouteToLocalStorage();

      const selectedCount = routePropertyIds.length;

      // Automatically create the route after loading the named route
      try {
        await createMultiPropertyRoute();
        alert(
          `Loaded and created route "${selectedNamedRoute.value}" with ${selectedCount} properties`,
        );
      } catch (error) {
        console.error(
          'Failed to create route after loading named route:',
          error,
        );
        alert(
          `Loaded route "${selectedNamedRoute.value}" with ${selectedCount} properties. Click "Visit Properties" to create the route.`,
        );
      }
    };

    // Clear the selected named route (remove all properties from it)
    const clearSelectedRoute = () => {
      if (
        !selectedNamedRoute.value ||
        !namedRoutes.value[selectedNamedRoute.value]
      ) {
        return;
      }

      // Remove the route from named routes
      delete namedRoutes.value[selectedNamedRoute.value];
      saveNamedRoutesToLocalStorage();

      // Clear the selection
      selectedNamedRoute.value = '';
      saveLastSelectedRouteToLocalStorage();

      // Clear any highlighting
      highlightedRoute.value = '';
      updateAllMarkerIcons();

      // Deselect all properties in the current selection
      properties.value.forEach((property) => {
        if (property.type === 'property') {
          property.includeInRoute = false;
        }
      });
      saveSelectionsToLocalStorage();

      // Clear any existing route on the map
      try {
        if (directionsRenderer) {
          // Properly clear directions by removing from map and recreating
          directionsRenderer.setMap(null);
          directionsRenderer = new window.google.maps.DirectionsRenderer({
            suppressMarkers: false,
            draggable: true,
          });
          directionsRenderer.setMap(map);
        }
        routeInfo.value = null;
      } catch (error) {
        console.error('Error clearing route in clearSelectedRoute:', error);

        // Fallback: try to recreate directions renderer completely
        try {
          if (map) {
            directionsRenderer = new window.google.maps.DirectionsRenderer({
              suppressMarkers: false,
              draggable: true,
            });
            directionsRenderer.setMap(map);
          }
        } catch (fallbackError) {
          console.error(
            'Fallback directions renderer creation failed in clearSelectedRoute:',
            fallbackError,
          );
        }

        // Set routeInfo to null even if directions renderer fails
        routeInfo.value = null;
      }

      // Close all open info windows (popup dialogs)
      closeAllInfoWindows();
    };

    // Add property to existing named route
    const addPropertyToNamedRoute = (propertyId, routeName) => {
      if (!routeName || !namedRoutes.value[routeName]) {
        return;
      }

      // Convert propertyId to number to ensure consistency
      const numericPropertyId = parseInt(propertyId);
      const property = properties.value.find((p) => p.id === numericPropertyId);
      if (!property) {
        return;
      }

      if (property.type !== 'property') {
        return;
      }

      // Check if property is already in the route
      if (namedRoutes.value[routeName].includes(numericPropertyId)) {
        return;
      }

      // Add property to the route
      namedRoutes.value[routeName].push(numericPropertyId);
      saveNamedRoutesToLocalStorage();
    };

    // Remove property from named route
    const removePropertyFromNamedRoute = (propertyId, routeName) => {
      if (!routeName || !namedRoutes.value[routeName]) {
        return;
      }

      // Convert propertyId to number to ensure consistency
      const numericPropertyId = parseInt(propertyId);
      const property = properties.value.find((p) => p.id === numericPropertyId);

      if (!property) {
        return;
      }

      // Remove property from the route
      const index = namedRoutes.value[routeName].indexOf(numericPropertyId);
      if (index > -1) {
        namedRoutes.value[routeName].splice(index, 1);
        saveNamedRoutesToLocalStorage();
      }
    };

    // Get routes that contain a specific property
    const getRoutesContainingProperty = (propertyId) => {
      const routesWithProperty = [];
      const numericPropertyId = parseInt(propertyId);
      Object.keys(namedRoutes.value).forEach((routeName) => {
        if (namedRoutes.value[routeName].includes(numericPropertyId)) {
          routesWithProperty.push(routeName);
        }
      });
      return routesWithProperty;
    };

    // Save named routes to localStorage
    const saveNamedRoutesToLocalStorage = () => {
      try {
        localStorage.setItem(
          'propertyMapperNamedRoutes',
          JSON.stringify(namedRoutes.value),
        );
        console.log('Named routes saved to localStorage');
      } catch (error) {
        console.error('Failed to save named routes to localStorage:', error);
      }
    };

    // Load named routes from localStorage
    const loadNamedRoutesFromLocalStorage = () => {
      try {
        const savedRoutes = localStorage.getItem('propertyMapperNamedRoutes');
        if (savedRoutes) {
          namedRoutes.value = JSON.parse(savedRoutes);
          console.log('Named routes loaded from localStorage');
        }
      } catch (error) {
        console.error('Failed to load named routes from localStorage:', error);
      }
    };

    // Save last selected route to localStorage
    const saveLastSelectedRouteToLocalStorage = () => {
      try {
        localStorage.setItem(
          'propertyMapperLastRoute',
          selectedNamedRoute.value,
        );
        console.log('Last selected route saved to localStorage');
      } catch (error) {
        console.error(
          'Failed to save last selected route to localStorage:',
          error,
        );
      }
    };

    // Load last selected route from localStorage
    const loadLastSelectedRouteFromLocalStorage = () => {
      try {
        const lastRoute = localStorage.getItem('propertyMapperLastRoute');
        if (lastRoute && namedRoutes.value[lastRoute]) {
          selectedNamedRoute.value = lastRoute;
          console.log('Last selected route loaded from localStorage');
        }
      } catch (error) {
        console.error(
          'Failed to load last selected route from localStorage:',
          error,
        );
      }
    };

    // Save property selections to localStorage
    const saveSelectionsToLocalStorage = () => {
      try {
        const selections = {};
        properties.value.forEach((property) => {
          selections[property.id] = property.includeInRoute;
        });
        localStorage.setItem(
          'propertyMapperSelections',
          JSON.stringify(selections),
        );
        console.log('Property selections saved to localStorage');
      } catch (error) {
        console.error('Failed to save selections to localStorage:', error);
      }
    };

    // Load property selections from localStorage
    const loadSelectionsFromLocalStorage = () => {
      try {
        const savedSelections = localStorage.getItem(
          'propertyMapperSelections',
        );
        if (savedSelections) {
          const selections = JSON.parse(savedSelections);
          properties.value.forEach((property) => {
            if (Object.prototype.hasOwnProperty.call(selections, property.id)) {
              property.includeInRoute = selections[property.id];
            }
          });
          console.log('Property selections loaded from localStorage');
          return true;
        }
        return false;
      } catch (error) {
        console.error('Failed to load selections from localStorage:', error);
        return false;
      }
    };

    // Initialize includeInRoute property for all existing properties
    const initializeIncludeInRoute = () => {
      // Load named routes first
      loadNamedRoutesFromLocalStorage();

      // First try to load from localStorage
      const loaded = loadSelectionsFromLocalStorage();

      // If no saved selections, set defaults
      if (!loaded) {
        properties.value.forEach((property) => {
          if (property.includeInRoute === undefined) {
            property.includeInRoute =
              property.type === 'property' || property.type === 'office';
          }
        });
      }

      // Load last selected route
      loadLastSelectedRouteFromLocalStorage();
    };

    // Initialize map
    const initializeMap = async () => {
      try {
        loading.value = true;
        error.value = null;

        await loadGoogleMapsAPI();

        await nextTick();

        const mapOptions = {
          zoom: 12,
          center: { lat: 40.7128, lng: -74.006 },
          mapTypeId: window.google.maps.MapTypeId.SATELLITE,
        };

        map = new window.google.maps.Map(
          document.getElementById('google-map'),
          mapOptions,
        );
        directionsService = new window.google.maps.DirectionsService();
        directionsRenderer = new window.google.maps.DirectionsRenderer({
          suppressMarkers: false,
          draggable: true,
        });
        directionsRenderer.setMap(map);
        geocoder = new window.google.maps.Geocoder();

        // Make createRoute function available globally for info window buttons
        window.createRouteToProperty = createRoute;

        // Make named route functions available globally for info window buttons
        window.addToSelectedRoute = (propertyId) => {
          const selectElement = document.getElementById(
            `routeSelect_${propertyId}`,
          );
          const routeName = selectElement?.value;

          if (routeName === '__NEW_ROUTE__') {
            // Handle creating a new route
            const newRouteName = prompt('Enter name for new route:');
            if (newRouteName && newRouteName.trim()) {
              const trimmedName = newRouteName.trim();
              // Check if route already exists
              if (namedRoutes.value[trimmedName]) {
                alert(
                  'A route with this name already exists. Please choose a different name.',
                );
                return;
              }

              // Create new route with this property
              namedRoutes.value[trimmedName] = [parseInt(propertyId)];
              saveNamedRoutesToLocalStorage();

              // Add property to route
              const property = properties.value.find((p) => p.id == propertyId);
              if (property && property.type === 'property') {
                property.includeInRoute = true;
                saveSelectionsToLocalStorage();
              }

              // Set this new route as the selected and highlighted route
              selectedNamedRoute.value = trimmedName;
              highlightedRoute.value = trimmedName;
              saveLastSelectedRouteToLocalStorage();
              updateAllMarkerIcons();

              alert(`Created new route "${trimmedName}" and added property`);

              // Refresh the popup by finding the marker and triggering a click
              setTimeout(() => {
                if (property) {
                  const marker = markers.find(
                    (m) => m.getTitle() === property.label,
                  );
                  if (marker) {
                    // Close current popup
                    markers.forEach(
                      (m) => m.infoWindow && m.infoWindow.close(),
                    );
                    // Trigger click to regenerate popup content
                    window.google.maps.event.trigger(marker, 'click');
                  }
                }
              }, 100);
            }
          } else if (routeName) {
            addPropertyToNamedRoute(propertyId, routeName);
            // Update highlighting if this route is currently highlighted
            if (highlightedRoute.value === routeName) {
              updateAllMarkerIcons();
            }

            // Synchronize selectedNamedRoute if we're adding to an unselected route
            if (
              !selectedNamedRoute.value ||
              selectedNamedRoute.value !== routeName
            ) {
              selectedNamedRoute.value = routeName;
              highlightedRoute.value = routeName;
              saveLastSelectedRouteToLocalStorage();
              updateAllMarkerIcons();
            }

            // Refresh the popup by finding the marker and triggering a click
            setTimeout(() => {
              const property = properties.value.find((p) => p.id == propertyId);
              if (property) {
                const marker = markers.find(
                  (m) => m.getTitle() === property.label,
                );
                if (marker) {
                  // Close current popup
                  markers.forEach((m) => m.infoWindow && m.infoWindow.close());
                  // Trigger click to regenerate popup content
                  window.google.maps.event.trigger(marker, 'click');
                }
              }
            }, 100);
          } else {
            alert('Please select a route');
          }
        };

        window.removeFromSelectedRoute = (propertyId) => {
          const selectElement = document.getElementById(
            `removeRouteSelect_${propertyId}`,
          );
          const routeName = selectElement?.value;
          if (routeName) {
            removePropertyFromNamedRoute(propertyId, routeName);
            // Update highlighting if this route is currently highlighted
            if (highlightedRoute.value === routeName) {
              updateAllMarkerIcons();
            }
            // Refresh the popup by finding the marker and triggering a click
            setTimeout(() => {
              const property = properties.value.find((p) => p.id == propertyId);
              if (property) {
                const marker = markers.find(
                  (m) => m.getTitle() === property.label,
                );
                if (marker) {
                  // Close current popup
                  markers.forEach((m) => m.infoWindow && m.infoWindow.close());
                  // Trigger click to regenerate popup content
                  window.google.maps.event.trigger(marker, 'click');
                }
              }
            }, 100);
          } else {
            alert('Please select a route');
          }
        };

        // Initialize includeInRoute property for all properties
        initializeIncludeInRoute();

        await geocodeAddresses();
        createMarkers();
        fitMapToMarkers();

        mapInitialized.value = true;
        loading.value = false;
      } catch (err) {
        console.error('Failed to initialize map:', err);
        error.value = err.message || 'Failed to initialize Google Maps';
        loading.value = false;
      }
    };

    onMounted(() => {
      initializeMap();
    });

    onUnmounted(() => {
      // Clean up global functions
      if (window.createRouteToProperty) {
        delete window.createRouteToProperty;
      }
      if (window.addToSelectedRoute) {
        delete window.addToSelectedRoute;
      }
      if (window.removeFromSelectedRoute) {
        delete window.removeFromSelectedRoute;
      }
    });

    return {
      loading,
      error,
      mapInitialized,
      showAddPropertyForm,
      showPropertyList,
      showSaveRouteForm,
      newRouteName,
      selectedNamedRoute,
      namedRoutes,
      routeInfo,
      searchQuery,
      filteredProperties,
      currentRouteBatch,
      totalBatches,
      hasMoreProperties,
      properties,
      newProperty,
      createMultiPropertyRoute,
      createNextBatch,
      getRemainingPropertiesCount,
      clearRoute,
      shareCurrentRoute,
      closeShareUrlDialog,
      selectAllUrl,
      copyUrlToClipboard,
      showShareUrlDialog,
      shareUrlDialogData,
      shareUrlInput,
      toggleAddPropertyForm,
      togglePropertyList,
      toggleSaveRouteForm,
      selectAllProperties,
      deselectAllProperties,
      getSelectedPropertiesCount,
      getSelectedProperties,
      saveNamedRoute,
      loadNamedRoute,
      clearSelectedRoute,
      addNewProperty,
      initializeMap,
      filterProperties,
      zoomToProperty,
      createRouteFromSearch,
      saveSelectionsToLocalStorage,
    };
  },
};
</script>

<style scoped>
.property-mapper {
  position: relative;
  max-width: 1400px;
  width: 100%;
  height: 100vh;
  font-family: Arial, sans-serif;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.loading-spinner {
  text-align: center;
}

.spinner {
  border: 4px solid #f3f3f3;
  border-top: 4px solid #4285f4;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.error-message {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.95);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.error-content {
  background: white;
  padding: 30px;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  text-align: center;
  max-width: 400px;
}

.error-content h3 {
  color: #ea4335;
  margin-top: 0;
}

.retry-button {
  background: #4285f4;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  cursor: pointer;
  margin-top: 15px;
}

.retry-button:hover {
  background: #3367d6;
}

.map-container {
  position: relative;
  width: 100%;
  height: 100%;
}

.controls-panel {
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 1000;
  background: white;
  padding: 15px;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  max-width: 250px;
}

.search-panel {
  position: absolute;
  top: 10px;
  left: 280px;
  z-index: 1000;
  background: white;
  padding: 15px;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  max-width: 300px;
}

.search-panel h3 {
  margin-top: 0;
  margin-bottom: 15px;
  color: #333;
}

.search-input-container {
  position: relative;
}

.search-input {
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-sizing: border-box;
  font-size: 14px;
}

.search-input:focus {
  outline: none;
  border-color: #4285f4;
}

.search-results {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #ddd;
  border-top: none;
  border-radius: 0 0 4px 4px;
  max-height: 300px;
  overflow-y: auto;
  z-index: 1001;
  /* Hide scrollbars while keeping scroll functionality */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* Internet Explorer 10+ */
}

.search-results::-webkit-scrollbar {
  display: none; /* Safari and Chrome */
}

.search-result-item {
  padding: 10px;
  cursor: pointer;
  border-bottom: 1px solid #eee;
}

.search-result-item:hover {
  background-color: #f5f5f5;
}

.search-result-item:last-child {
  border-bottom: none;
}

.search-result-content {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.search-result-button {
  background: #4285f4;
  color: white;
  padding: 4px 8px;
  border-radius: 3px;
  font-size: 11px;
  width: fit-content;
  cursor: pointer;
  margin-top: 5px;
}

.search-result-button:hover {
  background: #3367d6;
}

.search-result-item strong {
  color: #333;
}

.search-result-item small {
  color: #666;
  font-size: 12px;
}

.controls-panel h3 {
  margin-top: 0;
  margin-bottom: 15px;
  color: #333;
}

.route-batch-info {
  background: #e8f4fd;
  border: 1px solid #4285f4;
  border-radius: 4px;
  padding: 8px 12px;
  margin-bottom: 10px;
  text-align: center;
}

.route-batch-info p {
  margin: 0;
  font-size: 14px;
  font-weight: 500;
  color: #1a73e8;
}

.control-button {
  display: block;
  width: 100%;
  margin: 5px 0;
  padding: 10px;
  background: #4285f4;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.control-button:hover:not(:disabled) {
  background: #3367d6;
}

.control-button:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.named-routes-section {
  margin-top: 15px;
  padding-top: 15px;
  border-top: 1px solid #e0e0e0;
}

.named-route-select {
  width: 100%;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  margin-bottom: 8px;
  font-size: 14px;
  background: white;
}

.named-route-select:focus {
  outline: none;
  border-color: #4285f4;
}

.save-route-button {
  background: #34a853 !important;
}

.save-route-button:hover:not(:disabled) {
  background: #2d9e47 !important;
}

.clear-route-button {
  background: #ea4335 !important;
}

.clear-route-button:hover:not(:disabled) {
  background: #d33b2c !important;
}

.share-route-button {
  background: #ff9800 !important;
  color: white !important;
}

.share-route-button:hover:not(:disabled) {
  background: #e68900 !important;
}

.share-route-button:disabled {
  background: #cccccc !important;
  color: #999999 !important;
  cursor: not-allowed !important;
}

.save-route-form {
  position: absolute;
  bottom: 10px;
  right: 10px;
  z-index: 1000;
  background: white;
  padding: 15px;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  max-width: 300px;
  width: 300px;
}

.save-route-form h3 {
  margin-top: 0;
  margin-bottom: 15px;
  color: #333;
}

.selected-properties-preview {
  margin: 15px 0;
  padding: 10px;
  background: #f8f9fa;
  border-radius: 4px;
  border: 1px solid #e9ecef;
}

.selected-properties-preview p {
  margin: 0 0 8px 0;
  font-size: 14px;
  color: #333;
}

.selected-properties-list {
  max-height: 120px;
  overflow-y: auto;
  /* Hide scrollbars while keeping scroll functionality */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* Internet Explorer 10+ */
}

.selected-properties-list::-webkit-scrollbar {
  display: none; /* Safari and Chrome */
}

.selected-property-item {
  padding: 4px 8px;
  margin: 2px 0;
  background: white;
  border-radius: 3px;
  font-size: 12px;
  color: #555;
  border: 1px solid #dee2e6;
}

.route-info-panel {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 1000;
  background: white;
  padding: 15px;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  max-width: 350px;
  max-height: 500px;
  overflow-y: auto;
}

.route-info-panel h3 {
  margin-top: 0;
  color: #333;
}

.instructions-list {
  font-size: 12px;
  line-height: 1.4;
  max-height: 200px;
  overflow-y: auto;
}

.add-property-form {
  position: absolute;
  bottom: 10px;
  left: 10px;
  z-index: 1000;
  background: white;
  padding: 15px;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  max-width: 300px;
}

.property-list-panel {
  position: absolute;
  top: 10px;
  right: 380px;
  z-index: 1000;
  background: white;
  padding: 15px;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  width: 350px;
  max-height: 600px;
  display: flex;
  flex-direction: column;
}

.property-list-panel h3 {
  margin-top: 0;
  margin-bottom: 15px;
  color: #333;
  text-align: center;
}

.property-list-actions {
  display: flex;
  gap: 8px;
  margin-bottom: 15px;
  flex-wrap: wrap;
}

.list-action-button {
  flex: 1;
  padding: 8px 12px;
  background: #4285f4;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  min-width: 80px;
}

.list-action-button:hover {
  background: #3367d6;
}

.list-action-button.cancel {
  background: #ea4335;
}

.list-action-button.cancel:hover {
  background: #d33b2c;
}

.property-list-container {
  max-height: 450px;
  overflow-y: auto;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  /* Hide scrollbars while keeping scroll functionality */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* Internet Explorer 10+ */
}

.property-list-container::-webkit-scrollbar {
  display: none; /* Safari and Chrome */
}

.property-list-item {
  border-bottom: 1px solid #f0f0f0;
  padding: 0;
}

.property-list-item:last-child {
  border-bottom: none;
}

.property-list-item.office-item {
  background-color: #fff3cd;
}

.property-checkbox-container {
  display: flex;
  align-items: flex-start;
  padding: 12px;
  cursor: pointer;
}

.property-checkbox {
  margin-right: 10px;
  margin-top: 2px;
  cursor: pointer;
  transform: scale(1.1);
}

.property-checkbox:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.property-label {
  flex: 1;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.property-label.disabled {
  cursor: not-allowed;
  opacity: 0.7;
}

.property-name {
  font-weight: 500;
  color: #333;
  font-size: 14px;
}

.property-address {
  color: #666;
  font-size: 12px;
  line-height: 1.3;
}

.property-type {
  color: #999;
  font-size: 11px;
  font-style: italic;
  margin-top: 2px;
}

.property-checkbox-container:hover {
  background-color: #f8f9fa;
}

.property-list-item.office-item .property-checkbox-container:hover {
  background-color: #fff2b3;
}

.add-property-form h3 {
  margin-top: 0;
  color: #333;
}

.form-group {
  margin-bottom: 10px;
}

.form-input,
.form-select {
  width: 100%;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-sizing: border-box;
}

.form-buttons {
  display: flex;
  gap: 10px;
  margin-top: 15px;
}

.add-button {
  flex: 1;
  padding: 10px;
  background: #34a853;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.add-button:hover:not(:disabled) {
  background: #2d9e47;
}

.add-button:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.cancel-button {
  flex: 1;
  padding: 10px;
  background: #ea4335;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.cancel-button:hover {
  background: #d33b2c;
}

.google-map {
  width: 100%;
  height: 100%;
}

/* Hide scrollbars in Google Maps info windows */
:global(.gm-style-iw-d) {
  /* Hide scrollbars while keeping scroll functionality */
  scrollbar-width: none !important; /* Firefox */
  -ms-overflow-style: none !important; /* Internet Explorer 10+ */
}

:global(.gm-style-iw-d::-webkit-scrollbar) {
  display: none !important; /* Safari and Chrome */
}

/* Make Google Maps info window container semi-transparent - multiple selectors for reliability */
:global(.gm-style .gm-style-iw-c),
:global(.gm-style .gm-style-iw),
:global(.gm-style-iw),
:global(.gm-style [class*='iw']) {
  background: rgba(255, 255, 255, 0.85) !important;
  backdrop-filter: blur(5px) !important;
}

/* Target all inner content areas within Google Maps info windows */
:global(.gm-style-iw-c div),
:global(.gm-style-iw div),
:global(.gm-style [class*='iw'] div),
:global(.gm-style-iw-d),
:global(.gm-style-iw-d div) {
  background: rgba(255, 255, 255, 0.85) !important;
}

/* Additional fallback for any Google Maps popup containers */
:global(.gm-style div[style*='background']) {
  background: rgba(255, 255, 255, 0.85) !important;
}

/* Target any white backgrounds in Google Maps containers */
:global(.gm-style div[style*='background-color: white']),
:global(.gm-style div[style*='background-color: rgb(255, 255, 255)']),
:global(.gm-style div[style*='background: white']),
:global(.gm-style div[style*='background: rgb(255, 255, 255)']) {
  background: rgba(255, 255, 255, 0.85) !important;
}

/* Share URL Dialog Styles */
.share-url-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
}

.share-url-dialog {
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  max-width: 600px;
  width: 90%;
  max-height: 90%;
  overflow: hidden;
  animation: slideIn 0.3s ease-out;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: scale(0.9) translateY(-20px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.share-url-header {
  background: #4285f4;
  color: white;
  padding: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.share-url-header h3 {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
}

.share-url-header .close-btn {
  background: none;
  border: none;
  color: white;
  font-size: 24px;
  cursor: pointer;
  padding: 5px;
  width: 35px;
  height: 35px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.2s;
}

.share-url-header .close-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

.share-url-content {
  padding: 24px;
}

.route-details {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 20px;
  border-left: 4px solid #4285f4;
}

.route-details p {
  margin: 0 0 8px 0;
  font-size: 14px;
  color: #333;
}

.route-details p:last-child {
  margin-bottom: 0;
}

.url-section {
  margin-bottom: 16px;
}

.url-section label {
  display: block;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
  font-size: 14px;
}

.share-url-input {
  width: 100%;
  padding: 12px;
  font-size: 14px;
  border: 2px solid #e0e0e0;
  border-radius: 6px;
  background: #f8f9fa;
  color: #333;
  font-family: monospace;
  cursor: pointer;
  transition: all 0.2s;
  user-select: all; /* Allow easy text selection */
}

.share-url-input:hover {
  border-color: #4285f4;
  background: #fff;
}

.share-url-input:focus {
  outline: none;
  border-color: #4285f4;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(66, 133, 244, 0.1);
}

.share-url-status {
  margin-top: 12px;
}

.share-url-status p {
  margin: 0;
  font-size: 13px;
  padding: 8px 12px;
  border-radius: 4px;
}

.success-message {
  background: #e8f5e8;
  color: #2e7d32;
  border: 1px solid #c8e6c9;
}

.info-message {
  background: #e3f2fd;
  color: #1565c0;
  border: 1px solid #bbdefb;
}

.share-url-footer {
  background: #f8f9fa;
  padding: 16px 24px;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  border-top: 1px solid #e0e0e0;
}

.copy-btn {
  background: #4285f4;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
}

.copy-btn:hover {
  background: #3367d6;
}

.close-btn-secondary {
  background: #f1f3f4;
  color: #5f6368;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
}

.close-btn-secondary:hover {
  background: #e8eaed;
}
</style>
